import type { Contract } from '@/types/ContractType';
import type { PropertyStage } from '@/types/PropertyView';

export const SELLER_VISIBLE_STAGES: PropertyStage[] = [1, 2, 3, 4, 5, 6, 7];

export const SELLER_MAX_STAGE = 7 as PropertyStage;

const CLOSED_STATUSES = new Set<NonNullable<Contract['contractStatus']>>([
  'completed',
  'closed',
]);

const RATIFICATION_STATUSES = new Set<NonNullable<Contract['contractStatus']>>([
  'awaiting_signatures',
  'awaiting_buyer_signature',
  'awaiting_seller_signature',
  'negotiations_complete',
]);

const NEGOTIATION_STATUSES = new Set<NonNullable<Contract['contractStatus']>>([
  'draft',
  'submitted',
  'negotiating',
  'repair_negotiating',
]);

const INSPECTION_STATUSES = new Set<NonNullable<Contract['contractStatus']>>([
  'inspection_pending',
  'repair_requested',
]);

const ESCROW_STATUSES = new Set<NonNullable<Contract['contractStatus']>>([
  'ratified',
  'active',
  'escrow',
]);

export function sellerInfoComplete(
  contract: Contract | null | undefined
): boolean {
  return Boolean(contract?.sellerPropertyInfo?.isStored);
}

export interface ResolveSellerStageInput {
  contract?: Contract | null;
  openAddendumCount?: number;
  pipelineStageKey?: string;
}

export function resolveSellerStage(
  ctx: ResolveSellerStageInput
): PropertyStage {
  const { contract, openAddendumCount = 0, pipelineStageKey } = ctx;
  const status = contract?.contractStatus;

  if (
    status &&
    CLOSED_STATUSES.has(status) &&
    (pipelineStageKey === 'closing' ||
      pipelineStageKey === 'completed' ||
      pipelineStageKey === 'closed')
  ) {
    return 7;
  }

  if (status && CLOSED_STATUSES.has(status)) return 7;

  if (
    openAddendumCount > 0 ||
    (status === 'repair_negotiating' && contract?.isRatified) ||
    pipelineStageKey === 'addendum'
  ) {
    return 6;
  }

  if (status && INSPECTION_STATUSES.has(status)) {
    return 4;
  }

  if (contract?.isRatified || (status && ESCROW_STATUSES.has(status))) {
    return 5;
  }

  if (status && RATIFICATION_STATUSES.has(status)) return 3;

  if (status && NEGOTIATION_STATUSES.has(status)) return 2;

  if (!sellerInfoComplete(contract)) return 1;

  return 2;
}

export function visibleStagesForSeller(): PropertyStage[] {
  return SELLER_VISIBLE_STAGES;
}
